'use client';

import type { ReactNode } from 'react';
import { styled } from 'styled-components';
import cs from 'classnames';
import TextLink from '../components/TextLink';
import AccordionList from '../components/AccordionList';
import Media from '../components/Media';
import * as Styled from '../../lib/styles';

type MediaAccordionProps = {
  /**
   * What the H3 element says
   */
  headline: string;

  /**
   * Descriptive paragraph
   */
  description: string;

  /**
   * Accordion components passed in as children
   */
  children: ReactNode;
  /**
   * What href should the button/link point to?
   */
  href?: string;
  /**
   * What the link should say.
   */
  ctaText?: string;
  /**
   * The hashed id of the video; nesc. for showing video
   */
  hashedId?: string;
  /**
   * URL of image; nesc. for showing image
   */
  imageSrc?: string;
  /**
   * Alt text for image; nesc. for showing image
   */
  imageAlt?: string;
  /**
   *  Height of image; nesc. for showing image
   */
  imageHeight?: number;
  /**
   *  Width of image; nesc. for showing image
   */
  imageWidth?: number;
  /**
   * Layout
   * - standard (default layout)
   * - wide for large video
   */
  variant?: 'sd' | 'wide';
};

const MediaAccordionHeader = styled.div<{ $href?: string }>`
  padding-bottom: ${({ $href, theme }) =>
    $href ? theme.spacing['3'] : theme.spacing['1.5']};
  text-align: center;

  ${({ theme }) => theme.mq.minWidth.large} {
    padding-bottom: ${({ $href, theme }) =>
      $href ? theme.spacing['4'] : theme.spacing['2.5']};
  }
`;

const MediaAccordionContainer = styled.div`
  padding: ${({ theme }) => `${theme.spacing['4']} 0`};

  .mediaAccordionSplit {
    width: 100%;
    margin: 0;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    display: grid;
    ${({ theme }) => theme.grid.columns.large}

    ${MediaAccordionHeader} {
      grid-column: 3 / 11;
    }

    .mediaAccordionWrapper,
    .mediaAccordionSplit {
      grid-row: 2;
    }

    &.sd {
      .mediaAccordionWrapper {
        grid-column: 2 / span 5;
      }

      .mediaAccordionSplit {
        grid-column: 7 / 12;
      }
    }

    &.wide {
      .mediaAccordionWrapper {
        grid-column: 1 / span 5;
      }

      .mediaAccordionSplit {
        grid-column: 6 / 13;
      }
    }
  }
`;

const MediaAccordion = ({
  imageAlt,
  children,
  ctaText = 'Learn more',
  description,
  hashedId,
  headline,
  imageHeight,
  imageWidth,
  href,
  imageSrc,
  variant = 'sd',
}: MediaAccordionProps) => {
  return (
    <MediaAccordionContainer className={cs(variant)}>
      <MediaAccordionHeader $href={href}>
        <h2>{headline}</h2>
        <Styled.BodyLg>{description}</Styled.BodyLg>
        {href ? <TextLink href={href}>{ctaText}</TextLink> : undefined}
      </MediaAccordionHeader>

      {hashedId ? (
        <Media className="mediaAccordionSplit" hashedId={hashedId} />
      ) : (
        <Media
          alt={imageAlt}
          className="mediaAccordionSplit"
          height={imageHeight}
          src={imageSrc}
          width={imageWidth}
        />
      )}
      <AccordionList className="mediaAccordionWrapper">
        {children}
      </AccordionList>
    </MediaAccordionContainer>
  );
};

export default MediaAccordion;
